"use client";

import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
// import { SimpleSelect } from "@/components/ui/simple-select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import type { BaseEntity, SimpleTableConfig } from "./types";

// ============================================================================
// TYPES
// ============================================================================

interface SimpleTableMobilePaginationProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading?: boolean;
}

// ============================================================================
// MOBILE PAGINATION COMPONENT
// ============================================================================

function SimpleTableMobilePaginationInner<TData extends BaseEntity>({
  config,
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
}: SimpleTableMobilePaginationProps<TData>) {
  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    const hasItems = totalItems > 0;

    return {
      startItem,
      endItem,
      hasItems,
      canGoBack: currentPage > 1,
      canGoForward: currentPage < totalPages,
    };
  }, [currentPage, pageSize, totalItems, totalPages]);

  // Page size options - MEMOIZED
  const pageSizeOptions = useMemo(
    () => [10, 25, 50, 100, 200].filter((size) => size <= totalItems || totalItems === 0),
    [totalItems]
  );

  // Don't render if no items
  if (!paginationInfo.hasItems) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 p-3 bg-card border-t border-gray-200 dark:border-gray-700">
      {/* Pagination Info - Responsive */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
        <span className="truncate">
          {paginationInfo.startItem}-{paginationInfo.endItem} of {totalItems}
        </span>

        {/* Page Size Selector */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs sm:text-sm whitespace-nowrap">Show:</span>
          <select
            value={pageSize.toString()}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
            disabled={isLoading}
            className="w-16 h-7 px-1.5 py-1 border border-gray-300 dark:border-gray-700 rounded-md text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size.toString()}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Pagination Controls - Optimized for mobile */}
      <div className="flex items-center justify-between gap-2">
        {/* Previous Buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={!paginationInfo.canGoBack || isLoading}
            className="h-8 w-8 p-0 flex-shrink-0"
            aria-label="First page" title="First page"
          >
            <ChevronsLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!paginationInfo.canGoBack || isLoading}
            className="h-8 w-8 p-0 flex-shrink-0"
            aria-label="Previous page" title="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Page Numbers - Reduced for mobile */}
        <div className="flex-1 overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-1 justify-center min-w-max px-0.5">
            {(() => {
              const pages = [];
              const maxVisiblePages = 3; // Reduced from 5 to 3 for mobile
              const halfVisible = Math.floor(maxVisiblePages / 2);

              let startPage = Math.max(1, currentPage - halfVisible);
              let endPage = Math.min(totalPages, currentPage + halfVisible);

              // Adjust if we're near the beginning or end
              if (endPage - startPage + 1 < maxVisiblePages) {
                if (startPage === 1) {
                  endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                } else {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }
              }

              // Show first page if not in range
              if (startPage > 1) {
                pages.push(
                  <Button
                    key={1}
                    variant={currentPage === 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(1)}
                    disabled={isLoading}
                    className="h-8 min-w-[2rem] px-2 flex-shrink-0 text-xs"
                  >
                    1
                  </Button>
                );
                if (startPage > 2) {
                  pages.push(
                    <span key="ellipsis-start" className="px-1 text-muted-foreground text-xs flex-shrink-0">
                      ...
                    </span>
                  );
                }
              }

              // Add visible pages
              for (let page = startPage; page <= endPage; page++) {
                pages.push(
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    disabled={isLoading}
                    className="h-8 min-w-[2rem] px-2 flex-shrink-0 text-xs"
                  >
                    {page}
                  </Button>
                );
              }

              // Show last page if not in range
              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(
                    <span key="ellipsis-end" className="px-1 text-muted-foreground text-xs flex-shrink-0">
                      ...
                    </span>
                  );
                }
                pages.push(
                  <Button
                    key={totalPages}
                    variant={currentPage === totalPages ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(totalPages)}
                    disabled={isLoading}
                    className="h-8 min-w-[2rem] px-2 flex-shrink-0 text-xs"
                  >
                    {totalPages}
                  </Button>
                );
              }

              return pages;
            })()}
          </div>
        </div>

        {/* Next Buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!paginationInfo.canGoForward || isLoading}
            className="h-8 w-8 p-0 flex-shrink-0"
            aria-label="Next page" title="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={!paginationInfo.canGoForward || isLoading}
            className="h-8 w-8 p-0 flex-shrink-0"
            aria-label="Last page" title="Last page"
          >
            <ChevronsRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex items-center justify-center py-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-700 border-t-blue-600 rounded-full animate-spin" />
            Loading...
          </div>
        </div>
      )}
    </div>
  );
}

// React.memo erases the generic call signature; re-assert it via `typeof Inner`
// so consumers can pass `SimpleTableConfig<TData>` without an `unknown` cast.
export const SimpleTableMobilePagination = React.memo(
  SimpleTableMobilePaginationInner
) as typeof SimpleTableMobilePaginationInner;
